// CabinetAdminPage — ÉCRANS 8 & 9 de la maquette : dashboard cabinet (v5.20.0).
// Menu sombre à gauche (desktop) / nav horizontale (mobile) · contenu clair ·
// KPI compactes · Demandes récentes · Agenda du jour · Statistiques (funnel).
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { T, Avatar } from "@/components/cabinet/cabinetTheme";
import { getFounderToken, setFounderToken, clearFounderToken } from "@/lib/founderApi";
import { AdminTeam } from "@/components/cabinet/AdminTeam";
import { AdminClients } from "@/components/cabinet/AdminClients";
import { AdminParams, AdminDocuments, AdminReminders } from "@/components/cabinet/AdminPanels";
import { AdminContent } from "@/components/cabinet/AdminContent";
import { AyaProLogo } from "@/components/pro/AyaProLogo";
import { ProShareSheet } from "@/components/pro/ProShareCard";

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${getFounderToken()}` });
const PRIORITY = {
  haute: { label: "Haute", bg: "#FFEDD5", fg: "#C2410C" },
  normale: { label: "Normale", bg: "#DBEAFE", fg: "#1D4ED8" },
  basse: { label: "Basse", bg: "#F3F4F6", fg: "#6B7280" },
};
const NAV = [
  ["board", "🏠", "Tableau de bord"], ["requests", "📥", "Demandes"], ["agenda", "📅", "Rendez-vous"],
  ["visios", "📹", "Visios"], ["clients", "👥", "Clients & dossiers"], ["documents", "📄", "Documents"],
  ["paiements", "💳", "Paiements"], ["equipe", "🧑‍⚖️", "Équipe"], ["contenu", "📝", "Contenu"], ["stats", "📊", "Statistiques"], ["params", "⚙️", "Paramètres"],
];
const timeAgo = (iso) => {
  const m = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return `Il y a ${m} min`;
  if (m < 1440) return `Il y a ${Math.round(m / 60)} h`;
  return `Il y a ${Math.round(m / 1440)} j`;
};

export default function CabinetAdminPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [logged, setLogged] = useState(!!getFounderToken());
  const [tab, setTab] = useState("board");
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const onShare = () => setShareOpen(true);
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 900;

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/v3/cabinet/${slug}/admin/overview`, { headers: hdr() });
      setData(r.data);
      const s = await axios.get(`${API}/api/v3/cabinet/${slug}/admin/stats`, { headers: hdr() });
      setStats(s.data.stats);
    } catch (e) {
      if (e.response?.status === 401) { clearFounderToken(); setLogged(false); }
    }
  }, [slug]);

  useEffect(() => { if (logged) load(); }, [logged, load]);

  if (!logged) return <AdminLogin onLogged={() => setLogged(true)} />;
  if (!data) return <div style={{ ...T.page, display: "flex", alignItems: "center", justifyContent: "center", color: "#6B7280" }}>Chargement…</div>;

  const Sidebar = () => (
    <aside style={{ width: 216, background: "linear-gradient(175deg,#0B2E1D,#072415)", color: "#fff", padding: "16px 12px 12px", display: "flex", flexDirection: "column", gap: 2, flexShrink: 0, minHeight: "100vh" }} data-testid="admin-sidebar">
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "4px 0 16px" }}>
        <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(245,179,1,0.12)", border: "1px solid rgba(245,179,1,0.45)", display: "grid", placeItems: "center", fontSize: 25 }}>⚖️</div>
        <div style={{ fontSize: 13.5, fontWeight: 900, letterSpacing: 0.6, color: "#F2C66B" }}>{String(data.cabinet.name).toUpperCase()}</div>
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)" }}>Avocats & Conseil</div>
      </div>
      {NAV.map(([v, ic, l]) => (
        <button key={v} onClick={() => setTab(v)} data-testid={`admin-tab-${v}`}
          style={{
            display: "flex", gap: 9, alignItems: "center", padding: "9px 11px", borderRadius: 10, border: "none",
            fontSize: 12.5, fontWeight: 700, cursor: "pointer", textAlign: "left",
            background: tab === v ? "linear-gradient(180deg,#128A55,#0B6B40)" : "transparent", color: tab === v ? "#fff" : "rgba(255,255,255,0.7)",
            boxShadow: tab === v ? "0 3px 10px rgba(14,138,79,0.4)" : "none",
          }}><span style={{ fontSize: 13 }}>{ic}</span> {l}
          {v === "requests" && data.today.new_requests > 0 && <span style={{ marginLeft: "auto", minWidth: 17, padding: "1px 5px", borderRadius: 99, background: "#E6394B", color: "#fff", fontSize: 9.5, fontWeight: 900, textAlign: "center" }} data-testid="nav-requests-badge">{data.today.new_requests}</span>}
          {v === "clients" && data.unread_messages > 0 && <span style={{ marginLeft: "auto", minWidth: 16, padding: "0 4px", borderRadius: 99, background: "#F97316", color: "#fff", fontSize: 9.5, fontWeight: 900, textAlign: "center" }} data-testid="nav-unread-badge">{data.unread_messages}</span>}
        </button>
      ))}
      <div style={{ marginTop: "auto", borderRadius: 14, padding: "13px 13px 11px", background: "linear-gradient(160deg,rgba(245,179,1,0.16),rgba(245,179,1,0.05))", border: "1px solid rgba(245,179,1,0.35)" }} data-testid="admin-promo-card">
        <div style={{ fontSize: 12.5, fontWeight: 900, color: "#F2C66B" }}>👑 Développez votre cabinet</div>
        <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.65)", margin: "3px 0 9px", lineHeight: 1.4 }}>Attirez plus de clients depuis AYA Search</div>
        <button onClick={onShare} data-testid="admin-promote-btn"
          style={{ width: "100%", padding: "9px 0", borderRadius: 9, border: "none", background: "linear-gradient(180deg,#F7D98A,#EFB94C)", color: "#3A2A05", fontSize: 11.5, fontWeight: 900, cursor: "pointer" }}>Promouvoir mon cabinet</button>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, padding: "8px 10px", marginTop: 10 }} data-testid="admin-profile-card">
        <img src="/founder/allegra.jpg" alt="" style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover", border: "1.5px solid rgba(245,179,1,0.5)" }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Maître Allegra</div>
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)" }}>Administrateur</div>
        </div>
        <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 11 }}>▾</span>
      </div>
      <button onClick={() => { clearFounderToken(); setLogged(false); }} data-testid="admin-logout"
        style={{ marginTop: 8, padding: "9px 11px", borderRadius: 9, border: "1px solid rgba(255,255,255,0.2)", background: "none", color: "rgba(255,255,255,0.7)", fontSize: 12, cursor: "pointer" }}>🚪 Déconnexion</button>
      <div style={{ padding: "12px 8px 4px", display: "flex", alignItems: "center", gap: 7 }}>
        <span style={{ fontSize: 8.5, letterSpacing: 1, color: "rgba(255,255,255,0.4)", fontWeight: 700 }}>PROPULSÉ PAR</span>
        <AyaProLogo variant="compact" />
      </div>
    </aside>
  );

  const MobileNav = () => (
    <div style={{ background: "#0B2A1C", padding: "10px 12px", display: "flex", gap: 6, overflowX: "auto", scrollbarWidth: "none", alignItems: "center" }} data-testid="admin-mobile-nav">
      <span style={{ fontSize: 15, flexShrink: 0 }}>⚖️</span>
      {NAV.map(([v, , l]) => (
        <button key={v} onClick={() => setTab(v)} data-testid={`admin-tab-${v}`}
          style={{ ...T.chip, cursor: "pointer", flexShrink: 0, ...(tab === v ? { background: "#0E8A4F", color: "#fff", border: "1px solid #0E8A4F" } : { background: "rgba(255,255,255,0.1)", color: "#fff", border: "1px solid rgba(255,255,255,0.18)" }) }}>{l}</button>
      ))}
      <button onClick={onShare} data-testid="admin-promote-btn-mobile" style={{ ...T.chip, cursor: "pointer", flexShrink: 0, background: "#F5B301", color: "#1A1206", border: "1px solid #F5B301", fontWeight: 900 }}>📣 Promouvoir</button>
      <button onClick={() => { clearFounderToken(); setLogged(false); }} data-testid="admin-logout" style={{ ...T.chip, cursor: "pointer", flexShrink: 0, background: "none", color: "rgba(255,255,255,0.7)", border: "1px solid rgba(255,255,255,0.25)" }}>Sortir</button>
    </div>
  );

  const content = (
    <main style={{ flex: 1, padding: isDesktop ? "22px 26px" : "14px", background: "#F8FAF9", minHeight: "100vh" }}>
      {tab === "board" && <>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }} data-testid="admin-topbar">
          <div style={{ flex: 1, minWidth: 200, display: "flex", alignItems: "center", gap: 8, background: "#fff", border: "1px solid #E4ECE6", borderRadius: 999, padding: "9px 15px" }}>
            <span style={{ fontSize: 12, color: "#8CA096" }}>🔍</span>
            <input placeholder="Rechercher un dossier, un client, un rendez-vous…" data-testid="admin-search-input"
              onKeyDown={(e) => e.key === "Enter" && setTab("clients")}
              style={{ flex: 1, border: "none", outline: "none", fontSize: 12, background: "none", fontFamily: "inherit" }} />
            <span style={{ fontSize: 9.5, color: "#8CA096", fontWeight: 800 }}>⌘K</span>
          </div>
          <div style={{ position: "relative", width: 36, height: 36, borderRadius: "50%", border: "1px solid #E4ECE6", background: "#fff", display: "grid", placeItems: "center", cursor: "pointer" }} onClick={() => setTab("requests")} data-testid="admin-bell">
            🔔{data.today.new_requests > 0 && <span style={{ position: "absolute", top: 5, right: 6, width: 8, height: 8, borderRadius: "50%", background: "#E6394B" }} />}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7, background: "#fff", border: "1px solid #E4ECE6", borderRadius: 999, padding: "8px 14px", fontSize: 11.5, fontWeight: 800 }}>
            📅 <span style={{ textTransform: "capitalize" }}>{new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
          </div>
        </div>
        <AdminReminders slug={slug} />
        <div style={{ ...T.card, position: "relative", overflow: "hidden", padding: 0, marginBottom: 14, minHeight: 148 }} data-testid="admin-hero">
          {isDesktop && <>
            <img src="/founder/allegra.jpg" alt="" style={{ position: "absolute", right: 0, top: 0, height: "100%", width: "46%", objectFit: "cover", objectPosition: "center 18%" }} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, #fff 44%, rgba(255,255,255,0.92) 52%, rgba(255,255,255,0) 68%, rgba(6,30,18,0.45) 100%)" }} />
            <div style={{ position: "absolute", right: 18, top: 22, width: 205, zIndex: 2, color: "#fff", fontSize: 12, lineHeight: 1.55, fontStyle: "italic", textShadow: "0 1px 8px rgba(0,0,0,0.75)" }}>« Un accompagnement juridique de confiance pour vos projets en Côte d'Ivoire et en Afrique. »<div style={{ width: 34, height: 3, background: "#F2C66B", borderRadius: 2, marginTop: 7 }} /></div>
          </>}
          <div style={{ position: "relative", zIndex: 2, padding: isDesktop ? "22px 24px" : "16px 16px" }}>
            <h1 style={{ margin: 0, fontSize: isDesktop ? 25 : 19, fontWeight: 900, color: "#10241B", fontFamily: "Georgia,'Times New Roman',serif" }}>Bonjour Maître Allegra 👋</h1>
            <div style={{ fontSize: 13, color: "#5C7268", margin: "4px 0 11px" }}>Voici l'activité de votre cabinet aujourd'hui.</div>
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10.5, fontWeight: 800, background: "#E7F6EC", color: "#0E7A4B", borderRadius: 999, padding: "4px 11px" }}>✅ Cabinet vérifié AYA</span>
              {data.cabinet.demo_mode && <span style={{ fontSize: 10.5, fontWeight: 900, background: "#FFF3D6", color: "#92600A", borderRadius: 999, padding: "4px 11px" }} data-testid="admin-demo-badge">MODE DEMO</span>}
              <span style={{ fontSize: 10.5, fontWeight: 800, background: "#F1F5F2", color: "#5C7268", borderRadius: 999, padding: "4px 11px" }}>📍 {data.cabinet.commune}, {data.cabinet.city}</span>
            </div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "repeat(4, 1fr)" : "repeat(2, 1fr)", gap: 10, marginBottom: 14 }} data-testid="admin-kpis">
          {[["#E7F6EC", "#0E7A4B", "📥", data.today.new_requests, "Nouvelles demandes", "M0,18 L8,14 L16,16 L24,9 L32,12 L40,4"], ["#E3EEFD", "#2563EB", "📅", data.today.appointments, "Rendez-vous", "M0,16 L8,17 L16,12 L24,14 L32,8 L40,10"], ["#FFF0E2", "#D97706", "💼", data.today.in_progress, "Consultations en cours", "M0,17 L8,13 L16,15 L24,10 L32,11 L40,5"], ["#F1E9FD", "#7C3AED", "📹", data.today.visios, "Visios aujourd'hui", "M0,15 L8,16 L16,11 L24,13 L32,7 L40,9"]].map(([bg, fg, ic, v, l, path]) => (
            <div key={l} style={{ ...T.card, display: "flex", gap: 10, alignItems: "center", padding: "13px 14px" }}>
              <div style={{ width: 38, height: 38, borderRadius: 11, background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{ic}</div>
              <div style={{ flex: 1 }}>
                <div style={{ ...T.sub, fontSize: 10.5 }}>{l}</div>
                <div style={{ fontSize: 21, fontWeight: 900, lineHeight: 1.1 }}>{v}</div>
                <div style={{ fontSize: 10, fontWeight: 800, color: fg }}>↑ + {Math.max(1, Math.round(v / 2))} aujourd'hui</div>
              </div>
              <svg width="44" height="22" viewBox="0 0 40 20" style={{ flexShrink: 0 }}><path d={`${path} L40,20 L0,20 Z`} fill={bg} /><path d={path} fill="none" stroke={fg} strokeWidth="1.8" /></svg>
            </div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "1.6fr 1fr" : "1fr", gap: 12 }}>
          <RequestsPanel slug={slug} requests={data.recent_requests} members={data.members} onChanged={load} title="Demandes récentes" />
          <AgendaPanel agenda={data.agenda} navigate={navigate} slug={slug} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "1.25fr 1fr 1fr" : "1fr", gap: 12, marginTop: 12 }} data-testid="admin-board-bottom">
          <div style={T.card} data-testid="board-performance">
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <h2 style={{ ...T.h2, margin: 0 }}>📊 Performance du cabinet</h2>
              <span style={{ ...T.chip, marginLeft: "auto" }}>{stats ? `${stats.period_days} derniers jours` : "30 derniers jours"}</span>
            </div>
            {stats ? <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 12 }}>
                {stats.funnel.slice(0, 4).map((f) => (
                  <div key={f.id}><div style={{ fontSize: 16, fontWeight: 900 }}>{f.value.toLocaleString("fr-FR")}</div><div style={{ ...T.sub, fontSize: 9.5 }}>{f.label}</div><div style={{ fontSize: 9.5, fontWeight: 800, color: "#0E8A4F" }}>↑ {f.pct} %</div></div>
                ))}
              </div>
              <div style={{ ...T.sub, fontSize: 10.5, fontWeight: 800, marginBottom: 6 }}>Entonnoir d'acquisition (depuis AYA Search)</div>
              {stats.funnel.map((f, i) => (
                <div key={f.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 5 }}>
                  <span style={{ ...T.sub, fontSize: 10, width: 108, flexShrink: 0 }}>{f.label}</span>
                  <div style={{ flex: 1, height: 7, borderRadius: 99, background: "#EEF2F0", overflow: "hidden" }}><div style={{ height: "100%", width: `${Math.max(2, f.pct)}%`, background: ["#0E8A4F", "#2563EB", "#F5B301", "#7C3AED", "#DC2626", "#E6557B"][i % 6], borderRadius: 99 }} /></div>
                  <span style={{ fontSize: 10, fontWeight: 900, width: 72, textAlign: "right", flexShrink: 0 }}>{f.value.toLocaleString("fr-FR")} <span style={{ color: "#9CA3AF", fontWeight: 600 }}>({f.pct} %)</span></span>
                </div>
              ))}
            </> : <div style={T.sub}>Chargement…</div>}
          </div>
          <div style={T.card} data-testid="board-quick-actions">
            <h2 style={{ ...T.h2, marginBottom: 10 }}>⚡ Actions rapides</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8 }}>
              {[["requests", "📥", "Nouvelle demande", "Enregistrer une demande client", "#E7F6EC"], ["agenda", "📅", "Ajouter un rendez-vous", "Planifier une consultation", "#E3EEFD"], ["visios", "📹", "Démarrer une visio", "Salle de consultation", "#F1E9FD"], ["clients", "💬", "Voir les messages", "Consulter vos échanges", "#FDE9EF"], ["equipe", "👥", "Gérer l'équipe", "Membres et disponibilités", "#FFF0E2"], ["documents", "📄", "Documents", "Accéder à vos fichiers", "#E7F6EC"]].map(([t, ic, l, s, bg]) => (
                <button key={l} onClick={() => setTab(t)} data-testid={`quick-${t}`}
                  style={{ background: bg, border: "none", borderRadius: 13, padding: "13px 10px", textAlign: "center", cursor: "pointer", fontFamily: "inherit" }}>
                  <div style={{ fontSize: 19, marginBottom: 4 }}>{ic}</div>
                  <div style={{ fontSize: 11, fontWeight: 900, color: "#10241B" }}>{l}</div>
                  <div style={{ fontSize: 9, color: "#5C7268", marginTop: 2 }}>{s}</div>
                </button>
              ))}
            </div>
          </div>
          <div style={T.card} data-testid="board-activity">
            <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
              <h2 style={{ ...T.h2, margin: 0 }}>🕘 Activité récente</h2>
              <button onClick={() => setTab("requests")} style={{ marginLeft: "auto", background: "none", border: "none", color: "#0E8A4F", fontSize: 11, fontWeight: 800, cursor: "pointer" }}>Voir tout →</button>
            </div>
            {data.recent_requests.slice(0, 5).map((r) => (
              <div key={r.id} style={{ display: "flex", gap: 9, padding: "7px 0", borderBottom: "1px solid #F0F4F1" }}>
                <div style={{ width: 28, height: 28, borderRadius: 9, background: "#E7F6EC", display: "grid", placeItems: "center", fontSize: 12, flexShrink: 0 }}>📥</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 800, color: "#10241B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Nouvelle demande — {r.title || r.domain || "Demande de rendez-vous"}</div>
                  <div style={{ ...T.sub, fontSize: 10 }}>{r.domain} · {r.commune || "Abidjan"}</div>
                </div>
                <span style={{ ...T.sub, fontSize: 9.5, flexShrink: 0 }}>{timeAgo(r.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      </>}
      {tab === "requests" && <RequestsPanel slug={slug} requests={data.recent_requests} members={data.members} onChanged={load} title="Toutes les demandes" />}
      {tab === "agenda" && <AgendaPanel agenda={data.agenda} navigate={navigate} slug={slug} />}
      {tab === "visios" && <AgendaPanel agenda={data.agenda.filter((a) => a.mode === "visio")} navigate={navigate} slug={slug} title="Visios du jour" />}
      {tab === "equipe" && <AdminTeam slug={slug} members={data.members} onChanged={load} />}
      {tab === "contenu" && <AdminContent slug={slug} />}
      {tab === "clients" && <AdminClients slug={slug} onChanged={load} />}
      {tab === "documents" && <AdminDocuments slug={slug} />}
      {tab === "params" && <AdminParams slug={slug} />}
      {tab === "stats" && stats && <StatsPanel stats={stats} isDesktop={isDesktop} />}
      {tab === "paiements" && (
        <div style={{ ...T.card, ...T.sub }} data-testid="admin-placeholder">Ce module arrive dans une prochaine phase.</div>
      )}
    </main>
  );

  return (
    <div style={{ display: "flex", flexDirection: isDesktop ? "row" : "column", minHeight: "100vh", background: "#F8FAF9" }} data-testid="cabinet-admin-page">
      {isDesktop ? <Sidebar /> : <MobileNav />}
      {content}
      {shareOpen && (
        <ProShareSheet onClose={() => setShareOpen(false)}
          data={{
            name: data.cabinet.name, activityLabel: data.cabinet.tagline, icon: "⚖️", bg: "#EAF2FB",
            photo: data.cabinet.cover_photo, offerings: data.cabinet.domains, commune: data.cabinet.commune,
            kicker: "CABINET VIRTUEL", ctaTitle: "Retrouvez-nous sur AYA",
            ctaSub: "Rendez-vous en ligne et consultation en visio",
            waText: `\u2696\uFE0F *${data.cabinet.name}* — ${data.cabinet.tagline}\n\u2705 Cabinet vérifié AYA · \u{1F4CD} ${data.cabinet.commune}, ${data.cabinet.city}\n\n\u{1F4AC} Orientation par AYA, rendez-vous en ligne ou en visio :\n${window.location.origin}/cabinet/${slug}`,
          }} />
      )}
    </div>
  );
}

function AdminLogin({ onLogged }) {
  const [u, setU] = useState(""); const [p, setP] = useState(""); const [busy, setBusy] = useState(false);
  const login = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/founder/auth/login`, { username: u, password: p });
      setFounderToken(r.data.token);
      onLogged();
    } catch (e) { toast.error(e.response?.data?.message || "Identifiants incorrects"); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ ...T.page, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "#F8FAF9" }} data-testid="admin-login">
      <div style={{ ...T.card, width: "100%", maxWidth: 380, display: "grid", gap: 10, padding: 22 }}>
        <h1 style={{ ...T.h1, fontSize: 18 }}>⚖️ Espace cabinet</h1>
        <p style={{ ...T.sub, margin: 0 }}>Connectez-vous avec votre compte professionnel AYA.</p>
        <input style={{ ...T.input, borderRadius: 10 }} placeholder="Identifiant" value={u} onChange={(e) => setU(e.target.value)} data-testid="admin-login-username" />
        <input style={{ ...T.input, borderRadius: 10 }} type="password" placeholder="Mot de passe" value={p} onChange={(e) => setP(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()} data-testid="admin-login-password" />
        <button style={{ ...T.btnGreen, opacity: busy ? 0.6 : 1 }} onClick={login} disabled={busy} data-testid="admin-login-submit">{busy ? "Connexion…" : "Se connecter"}</button>
      </div>
    </div>
  );
}

function RequestsPanel({ slug, requests, members, onChanged, title }) {
  const assign = async (id, member_id) => {
    try {
      await axios.patch(`${API}/api/v3/cabinet/${slug}/admin/requests/${id}`, { assigned_member_id: member_id || null, status: member_id ? "assigned" : "new" }, { headers: hdr() });
      toast.success("Demande attribuée ✓"); onChanged();
    } catch { toast.error("Échec de l'attribution"); }
  };
  const setStatus = async (id, status) => {
    try { await axios.patch(`${API}/api/v3/cabinet/${slug}/admin/requests/${id}`, { status }, { headers: hdr() }); onChanged(); }
    catch { toast.error("Échec"); }
  };
  return (
    <div style={T.card} data-testid="admin-requests">
      <h2 style={T.h2}>{title}</h2>
      <div style={{ display: "grid", gap: 0, marginTop: 4 }}>
        {requests.map((r) => {
          const pr = PRIORITY[r.priority] || PRIORITY.normale;
          return (
            <div key={r.request_id} style={{ padding: "10px 0", borderBottom: "1px solid #F3F4F6" }} data-testid={`admin-request-${r.request_id}`}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ width: 8, height: 8, borderRadius: 99, background: pr.fg, flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 800, flex: 1, minWidth: 120 }}>{r.subject}</span>
                <span style={{ ...T.badge, background: pr.bg, color: pr.fg }}>{pr.label}</span>
                <span style={{ ...T.sub, fontSize: 11 }}>{timeAgo(r.created_at)}</span>
              </div>
              <div style={{ ...T.sub, fontSize: 11.5, margin: "2px 0 7px", paddingLeft: 16 }}>
                {r.qualification?.domain || "—"} {r.commune ? `· ${r.commune}` : ""} · {r.client_name}
                {r.source === "AYA_SEARCH" && <span style={{ ...T.badge, ...T.badgeOrange, marginLeft: 6 }} data-testid={`request-source-${r.request_id}`}>via AYA Search</span>}
                {r.is_demo && <span style={{ ...T.badge, ...T.badgeDemo, marginLeft: 6 }}>DEMO</span>}
              </div>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center", paddingLeft: 16 }}>
                <select value={r.assigned_member_id || ""} onChange={(e) => assign(r.request_id, e.target.value)}
                  style={{ padding: "5px 9px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 11.5, background: "#fff", color: "#374151" }} data-testid={`request-assign-${r.request_id}`}>
                  <option value="">Attribuer à…</option>
                  {members.filter((m) => m.active !== false).map((m) => <option key={m.member_id} value={m.member_id}>{m.name}</option>)}
                </select>
                {r.status !== "in_progress" && <button style={{ ...T.btnGreen, padding: "5px 13px", fontSize: 11.5 }} onClick={() => setStatus(r.request_id, "in_progress")} data-testid={`request-treat-${r.request_id}`}>Traiter</button>}
                {r.status !== "closed" && <button style={{ background: "none", border: "none", color: "#6B7280", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }} onClick={() => setStatus(r.request_id, "closed")} data-testid={`request-close-${r.request_id}`}>Clôturer</button>}
              </div>
            </div>
          );
        })}
        {requests.length === 0 && <div style={{ ...T.sub, paddingTop: 8 }}>Aucune demande pour le moment.</div>}
      </div>
      <div style={{ color: "#0E8A4F", fontSize: 12, fontWeight: 800, paddingTop: 10 }}>Voir toutes les demandes →</div>
    </div>
  );
}

function AgendaPanel({ agenda, navigate, slug, title = "Agenda du jour" }) {
  return (
    <div style={T.card} data-testid="admin-agenda">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={T.h2}>{title}</h2>
        <span style={{ color: "#0E8A4F", fontSize: 11.5, fontWeight: 800 }}>Voir tout</span>
      </div>
      <div style={{ display: "grid", gap: 0, marginTop: 4 }}>
        {agenda.map((a) => (
          <div key={a.appointment_id} style={{ display: "flex", gap: 9, alignItems: "center", padding: "9px 0", borderBottom: "1px solid #F3F4F6" }} data-testid={`agenda-item-${a.appointment_id}`}>
            <div style={{ fontSize: 12.5, fontWeight: 900, color: "#0E8A4F", width: 42, flexShrink: 0 }}>{a.time}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 800 }}>{a.mode === "visio" ? "Visio" : "Consultation"} — {a.type.replace("Consultation d'orientation — ", "")}</div>
              <div style={{ ...T.sub, fontSize: 11 }}>{a.member_name} · {a.client_name}{a.is_demo ? " · DEMO" : ""}</div>
            </div>
            {a.status === "in_progress"
              ? <span style={{ ...T.badge, ...T.badgeGreen }}>En cours</span>
              : a.mode === "visio" && !a.is_demo
                ? <button style={{ background: "none", border: "none", color: "#0E8A4F", fontSize: 11.5, fontWeight: 800, cursor: "pointer" }} onClick={() => navigate(`/cabinet/${slug}/visio/${a.appointment_id}`)} data-testid={`agenda-join-${a.appointment_id}`}>À venir →</button>
                : <span style={{ ...T.sub, fontSize: 11 }}>À venir</span>}
          </div>
        ))}
        {agenda.length === 0 && <div style={{ ...T.sub, paddingTop: 8 }}>Aucun rendez-vous aujourd'hui.</div>}
      </div>
    </div>
  );
}

function StatsPanel({ stats, isDesktop }) {
  const max = stats.funnel[0]?.value || 1;
  const COLORS = ["#0E8A4F", "#2563EB", "#F97316", "#9333EA", "#DC2626", "#CA8A04"];
  return (
    <div style={{ display: "grid", gap: 12 }} data-testid="admin-stats">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
        <h1 style={{ ...T.h1, fontSize: 17 }}>Performance du cabinet</h1>
        <span style={{ ...T.chip }} data-testid="stats-mode">{stats.period_days} derniers jours · {stats.mode}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "repeat(3, 1fr)" : "repeat(2, 1fr)", gap: 10 }} data-testid="stats-kpis">
        {stats.funnel.map((f) => (
          <div key={f.id} style={{ ...T.card, padding: "13px 14px" }}>
            <div style={{ fontSize: 21, fontWeight: 900, lineHeight: 1 }}>{f.value.toLocaleString("fr-FR")}</div>
            <div style={{ ...T.sub, fontSize: 11, margin: "4px 0 3px" }}>{f.label}</div>
            {f.real > 0 && <div style={{ color: "#0E8A4F", fontSize: 10.5, fontWeight: 800 }}>↑ dont {f.real.toLocaleString("fr-FR")} réel</div>}
          </div>
        ))}
      </div>
      <div style={T.card}>
        <h2 style={T.h2}>Entonnoir d'acquisition</h2>
        <div style={{ ...T.sub, fontSize: 11.5, marginBottom: 10 }}>De la recherche à la consultation</div>
        <div style={{ display: "grid", gap: 9 }}>
          {stats.funnel.map((f, i) => (
            <div key={f.id} style={{ display: "flex", gap: 10, alignItems: "center" }} data-testid={`funnel-${f.id}`}>
              <span style={{ ...T.sub, fontSize: 11.5, width: isDesktop ? 180 : 110, flexShrink: 0 }}>{f.label}</span>
              <div style={{ flex: 1, height: 8, borderRadius: 99, background: "#EEF2F0", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.max(2, (f.value / max) * 100)}%`, background: COLORS[i % COLORS.length], borderRadius: 99 }} />
              </div>
              <span style={{ fontSize: 11.5, fontWeight: 900, width: 86, textAlign: "right", flexShrink: 0 }}>
                {f.value.toLocaleString("fr-FR")} <span style={{ color: "#9CA3AF", fontWeight: 600 }}>({f.pct} %)</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
